import React, { useMemo, useState, useEffect, useCallback, useId } from 'react';
import PropTypes from 'prop-types';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Brush
} from 'recharts';
import { chartTheme, getSeriesColor } from './chartTheme';
import { buildAreaGradientDefs } from './chartGradientUtils';
import {
  defaultDateTickFormatter,
  defaultDateTooltipFormatter,
  defaultValueFormatter
} from './formatters';
import { YChartTooltip } from './YChartTooltip';
import { buildBrushDetails } from './rangeUtils';
import { toggleLegendKey } from './legendUtils';

export function YAreaChart({
  data = [],
  height = 240,
  xKey = 'date',
  xTickFormatter = defaultDateTickFormatter,
  tooltipLabelFormatter = defaultDateTooltipFormatter,
  tooltipValueFormatter = defaultValueFormatter,
  yAxis = {},
  series = [],
  legend = false,
  enableLegendToggle = true,
  onLegendToggle,
  brush,
  margin = { top: 10, right: 20, left: 10, bottom: 10 },
  syncId,
  dataTestId,
  gradientInstanceId
}) {
  const reactId = useId();
  const instanceId = gradientInstanceId || reactId.replace(/:/g, '');

  const initialActive = useMemo(() => {
    if (!Array.isArray(series) || series.length === 0) return [];
    return series.map((item) => item.dataKey || item.name).filter(Boolean);
  }, [series]);

  const [activeKeys, setActiveKeys] = useState(initialActive);

  useEffect(() => {
    setActiveKeys(initialActive);
  }, [initialActive]);

  const gradientDefs = useMemo(
    () => buildAreaGradientDefs(series, instanceId),
    [series, instanceId]
  );

  const resolvedSeries = useMemo(() => {
    return series.map((item, index) => {
      const stroke = getSeriesColor(index, item.stroke || item.color);
      const grad = gradientDefs[index];
      const fill = grad ? `url(#${grad.id})` : stroke;
      return {
        type: 'monotone',
        strokeWidth: item.strokeWidth ?? (index === 0 ? 2 : 1.5),
        strokeDasharray: item.strokeDasharray,
        dot: item.dot ?? false,
        activeDot: item.activeDot ?? { r: 5, strokeWidth: 2 },
        ...item,
        stroke,
        fill,
        dataKey: item.dataKey || item.name
      };
    });
  }, [series, gradientDefs]);

  const handleLegendClick = useCallback(
    (entry) => {
      const key = entry?.dataKey || entry?.value;
      setActiveKeys((prev) => {
        const next = toggleLegendKey(prev, key);
        if (next !== prev && onLegendToggle) {
          onLegendToggle(next);
        }
        return next;
      });
    },
    [onLegendToggle]
  );

  const legendFormatter = useCallback(
    (value, entry) => {
      const key = entry?.dataKey || value;
      const isActive = !enableLegendToggle || activeKeys.includes(key);
      return <span style={{ opacity: isActive ? 1 : 0.5 }}>{value}</span>;
    },
    [activeKeys, enableLegendToggle]
  );

  const handleBrushChange = (range) => {
    const details = buildBrushDetails(range, data, xKey);
    if (brush?.onChange) brush.onChange(details);
  };

  const hasData = Array.isArray(data) && data.length > 0;
  const hasSelection =
    typeof brush?.selection?.startIndex === 'number' &&
    typeof brush?.selection?.endIndex === 'number';
  const defaultEndIndex = hasData ? data.length - 1 : undefined;
  const brushStartIndex = hasData
    ? hasSelection
      ? brush.selection.startIndex
      : 0
    : undefined;
  const brushEndIndex = hasData
    ? hasSelection
      ? brush.selection.endIndex
      : defaultEndIndex
    : undefined;

  return (
    <div className="y-chart y-chart--area" style={{ height }} data-testid={dataTestId}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={margin} syncId={syncId}>
          <defs>
            {gradientDefs.map((grad) => (
              <linearGradient key={grad.id} id={grad.id} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={grad.stroke} stopOpacity={grad.topOpacity} />
                <stop offset="95%" stopColor={grad.stroke} stopOpacity={grad.bottomOpacity} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.gridColor} />
          <XAxis
            dataKey={xKey}
            tickFormatter={xTickFormatter}
            axisLine={false}
            tickLine={chartTheme.tickLine}
            stroke={chartTheme.axisColor}
            fontSize={chartTheme.axisFontSize}
          />
          <YAxis
            axisLine={false}
            tickLine={chartTheme.tickLine}
            stroke={chartTheme.axisColor}
            fontSize={chartTheme.axisFontSize}
            {...yAxis}
          />
          <Tooltip
            cursor={false}
            content={
              <YChartTooltip
                labelFormatter={tooltipLabelFormatter}
                valueFormatter={tooltipValueFormatter}
              />
            }
          />
          {legend && (
            <Legend
              verticalAlign="top"
              wrapperStyle={{ paddingBottom: 8, fontSize: 11, fontFamily: 'var(--font-mono)' }}
              iconType="circle"
              onClick={enableLegendToggle ? handleLegendClick : undefined}
              formatter={legendFormatter}
            />
          )}
          {resolvedSeries.map((area) => {
            const key = area.dataKey || area.name;
            const isActive = !legend || activeKeys.includes(key);
            return <Area key={key} {...area} hide={!isActive} />;
          })}
          {brush?.enabled && hasData && (
            <Brush
              key={brush.version || 0}
              dataKey={xKey}
              height={24}
              stroke={chartTheme.accent}
              travellerWidth={10}
              startIndex={brushStartIndex}
              endIndex={brushEndIndex}
              onChange={handleBrushChange}
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

YAreaChart.propTypes = {
  data: PropTypes.array,
  height: PropTypes.number,
  xKey: PropTypes.string,
  xTickFormatter: PropTypes.func,
  tooltipLabelFormatter: PropTypes.func,
  tooltipValueFormatter: PropTypes.func,
  yAxis: PropTypes.object,
  series: PropTypes.arrayOf(PropTypes.object),
  legend: PropTypes.bool,
  enableLegendToggle: PropTypes.bool,
  brush: PropTypes.shape({
    enabled: PropTypes.bool,
    selection: PropTypes.shape({
      startIndex: PropTypes.number,
      endIndex: PropTypes.number
    }),
    onChange: PropTypes.func,
    version: PropTypes.number
  }),
  margin: PropTypes.object,
  syncId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  dataTestId: PropTypes.string,
  onLegendToggle: PropTypes.func,
  gradientInstanceId: PropTypes.string
};

export default YAreaChart;
